import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { faAngleRight, faPiggyBank } from '@fortawesome/free-solid-svg-icons';
import { ISettlement, ScrollDirection } from '../../models';
import { take } from 'rxjs';
import { PaginationHelperService } from 'libs/odin-core/src/lib/services/pagination-helper.service';
import { RouterExtService } from '../../services/routing.service';
import { SettlementsFacade } from '../../../+state/settlements/settlements.facade';

@Component({
  standalone: false,
  selector: 'odin-settlements-list',
  templateUrl: './settlements-list.component.html',
  styleUrls: ['./settlements-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettlementsListComponent implements AfterViewInit, OnInit {
  // Keep in sync with $row-height (+1px divider) in the component scss.
  public rowSize = 64;
  // Keep a few rows rendered beyond each viewport edge so fast scrolling
  // doesn't reveal blank strips while the CDK catches up.
  public minBufferPx = this.rowSize * 6;
  public maxBufferPx = this.rowSize * 12;
  @ViewChild(CdkVirtualScrollViewport)
  virtualScroll: CdkVirtualScrollViewport | null = null;

  @Input() settlements: ISettlement[] = [];

  scrollDisable = false;
  @Input() set loading(value: boolean | null) {
    this.scrollDisable = value || false;
  }

  @Input() allowSelection: boolean | null = true;
  @Input() hasNext: boolean | null = false;
  @Input() hasPrev: boolean | null = false;
  @Output() navigateToDetails: EventEmitter<ISettlement> =
    new EventEmitter<ISettlement>();
  @Output() scrollDirection: EventEmitter<ScrollDirection> =
    new EventEmitter<ScrollDirection>();

  faAngleRight = faAngleRight;
  faSettled = faPiggyBank;

  infiniteScrollDistance = 1.4;
  infiniteScrollThrottle = 150;
  fromRoot = true;

  constructor(
    private paginationHelper: PaginationHelperService,
    private routerExtService: RouterExtService,
    private facade: SettlementsFacade
  ) { }

  ngOnInit(): void {
    if (!this.routerExtService.redirectFromSettlementDetails()) {
      this.facade.settlementsScrollPosition(0);
    }
  }

  public ngAfterViewInit(): void {
    if (!this.virtualScroll) return;

    // Restore the position saved when the user opened a settlement's detail
    // page. Deferred a single tick (not a fixed delay) so the viewport has
    // measured its content before we scroll; a longer delay let the list paint
    // at the top first and then visibly jump.
    this.facade.getScrollPosition$
      .pipe(take(1))
      .subscribe((scrollPosition: number) => {
        const posToScroll = scrollPosition || 0;
        if (posToScroll <= 0) return;

        setTimeout(() => {
          if (!this.virtualScroll) return;
          this.virtualScroll.scrollToOffset(posToScroll);
        });
      });

    this.virtualScroll.elementScrolled().subscribe(() => {
      if (!this || !this.virtualScroll) return;

      const distFromBottom = this.virtualScroll.measureScrollOffset('bottom');

      this.paginationHelper.detectEndOfScrollRaw(
        distFromBottom,
        this.rowSize,
        5,
        () => {
          if (this.hasNext) {
            this.scrollDirection.emit(ScrollDirection.Down);
          }
        }
      );
    });
  }

  trackRow(_index: number, settlement: ISettlement): number {
    return new Date(settlement.settledDay).getTime();
  }

  select(settlement: ISettlement): void {
    if (!this.allowSelection) return;
    if (
      settlement.grossAmount === 0 &&
      settlement.totalFeeAmount === 0 &&
      settlement.netAmount === 0
    ) return;
    if (this.virtualScroll == null) return;

    const scrollTop = this.virtualScroll.measureScrollOffset('top');
    this.facade.settlementsScrollPosition(scrollTop);
    this.navigateToDetails.emit(settlement);
  }
}
